import { Button } from 'system/atoms';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { Fields, Form, FormRef, FormOption } from 'common/form';
import {
  CandidateStatus,
  StatusSet,
  UpdateCandidateInput
} from 'generated-graphql/types';
import { getStatusOptions } from 'modules/analyse/utils/getStatusOptions';
import { useRecoilValue } from 'recoil';
import { statusSetAtom } from 'services/candidate/atoms';
import Yup from 'utils/validation';
import { useAnalysisCandidateATSOptions } from '../hooks/useAnalysisCandidateATSOptions';
import { useEffect, useRef, useState } from 'react';
import { useUpdateCandidateMutation } from 'generated-graphql/hooks/apollo-hooks';

type GetAnalysisCandidateStatusSchemaProps = {
  atsJobId: string | null | undefined;
};

const getAnalysisCandidateStatusSchema = ({
  atsJobId
}: GetAnalysisCandidateStatusSchemaProps) =>
  Yup.object().shape({
    status: Yup.string().required('Status is required'),
    isLocked: Yup.boolean().required('Lock status is required'),
    atsStatusId: atsJobId
      ? Yup.string().required('ATS status is required')
      : Yup.string()
  });

export type AnalysisCandidateStatusFormData = Yup.InferType<
  ReturnType<typeof getAnalysisCandidateStatusSchema>
>;

type GetAnalysisCandidateStatusFieldsProps = {
  statusSet: StatusSet | null;
  atsOptions: FormOption[];
};

const getAnalysisCandidateStatusFields = ({
  statusSet,
  atsOptions
}: GetAnalysisCandidateStatusFieldsProps): Fields<AnalysisCandidateStatusFormData> => ({
  status: {
    type: 'radio',
    options: getStatusOptions({ statusSet })
  },
  isLocked: {
    type: 'checkbox',
    checkboxLabel: 'Lock this status'
  },
  atsStatusId: {
    type: 'select',
    label: 'Update [ATS name] status',
    placeholder: 'Choose',
    options: atsOptions,
    hidden: atsOptions.length === 0
  }
});

type CandidateProfileAnalysisChangeStatusProps = {
  candidateId: string;
  atsJobId: string | null | undefined;
  defaultStatus: CandidateStatus;
  defaultIsLocked: boolean;
};

export const CandidateProfileAnalysisChangeStatus: React.FC<
  CandidateProfileAnalysisChangeStatusProps
> = ({ candidateId, atsJobId, defaultStatus, defaultIsLocked }) => {
  const statusSet = useRecoilValue(statusSetAtom);
  // NOTE: This is used to update the ATS Options
  const [status, setStatus] = useState<CandidateStatus>(defaultStatus);
  const { openDrawer, closeDrawer } = useDrawerV2();
  const formRef = useRef<FormRef<AnalysisCandidateStatusFormData>>(null);
  const {
    atsOptions,
    interviewStages,
    rejectedReasons,
    useStagesForRejection
  } = useAnalysisCandidateATSOptions({
    atsJobId,
    status
  });

  const [updateCandidate] = useUpdateCandidateMutation();

  useEffect(() => {
    if (!formRef.current) {
      return;
    }
    formRef.current.watch('status', (value: CandidateStatus) => {
      setStatus(value);
    });
  }, []);

  const getUpdateCandidateInput = (
    values: AnalysisCandidateStatusFormData
  ): UpdateCandidateInput => {
    if (!values.atsStatusId) {
      return {
        id: candidateId,
        status: values.status,
        isLocked: values.isLocked
      };
    }

    if (values.status === CandidateStatus.Rejected && !useStagesForRejection) {
      const reason = rejectedReasons.find((r) => r.id === values.atsStatusId);
      return {
        id: candidateId,
        status: values.status,
        atsRejectedReasonId: reason?.remoteId || values.atsStatusId,
        komboRejectionReasonId: reason?.remoteId
          ? values.atsStatusId
          : undefined,
        isLocked: values.isLocked
      };
    }

    const stage = interviewStages.find((s) => s.id === values.atsStatusId);
    return {
      id: candidateId,
      status: values.status,
      atsInterviewStageId: stage?.remoteId || values.atsStatusId,
      komboStageId: stage?.remoteId ? values.atsStatusId : undefined,
      isLocked: values.isLocked
    };
  };

  const onSubmit = async (values: AnalysisCandidateStatusFormData) => {
    const input = getUpdateCandidateInput(values);

    await updateCandidate({
      variables: {
        input
      }
    });

    closeDrawer();
  };

  return (
    <Button
      variant="tertiary"
      sx={{ padding: '0' }}
      onClick={() =>
        openDrawer({
          title: 'Change status',
          children: (
            <Form
              sx={{
                pt: '20px'
              }}
              ref={formRef}
              defaultValues={{
                status: defaultStatus,
                isLocked: defaultIsLocked
              }}
              schema={getAnalysisCandidateStatusSchema({ atsJobId })}
              fields={getAnalysisCandidateStatusFields({
                statusSet,
                atsOptions
              })}
              submitButtonProps={{
                label: 'Confirm'
              }}
              onSubmit={onSubmit}
            />
          )
        })
      }
    >
      Status
    </Button>
  );
};
